/**
 * AffiliatePortalPage — an affiliate's own corner, reached by the magic link
 * emailed to them. No password: the token in the URL is the key.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CreditCard, Loader2, Send, ShieldCheck, AlertTriangle, Clock, Inbox, Download,
} from 'lucide-react';

import api, { getErrorMessage, API_BASE } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import FwmShell from '@/components/fwm/FwmShell';
import { formatCurrency } from '@/lib/currency';

const STATUS = {
  active: { label: 'Active affiliate', tone: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: ShieldCheck },
  awaiting_payment: { label: 'Awaiting payment', tone: 'text-amber-700 bg-amber-50 border-amber-200', icon: Clock },
  lapsed: { label: 'Lapsed', tone: 'text-rose-700 bg-rose-50 border-rose-200', icon: AlertTriangle },
  pending: { label: 'Under review', tone: 'text-slate-700 bg-slate-50 border-slate-200', icon: Clock },
  rejected: { label: 'Not approved', tone: 'text-rose-700 bg-rose-50 border-rose-200', icon: AlertTriangle },
};

export default function AffiliatePortalPage() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [reply, setReply] = useState({ subject: '', body: '' });
  const [paying, setPaying] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['affiliate-portal', token],
    queryFn: () => api.get(`/affiliates/portal/${token}`).then((r) => r.data),
    retry: false,
  });

  const paidSession = params.get('paid');
  useEffect(() => {
    if (!paidSession) return;
    let cancelled = false;
    const check = async (attempt = 0) => {
      try {
        const { data: res } = await api.get(
          `/affiliates/portal/${token}/payment-status/${paidSession}`);
        if (cancelled) return;
        if (res.paid) {
          toast.success('Payment confirmed — your affiliation is active');
          params.delete('paid');
          setParams(params, { replace: true });
          qc.invalidateQueries({ queryKey: ['affiliate-portal', token] });
        } else if (attempt < 5) {
          setTimeout(() => check(attempt + 1), 2500);
        }
      } catch {
        /* the invoice may already be settled — the reload below shows the truth */
      }
    };
    check();
    return () => { cancelled = true; };
  }, [paidSession, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = useMutation({
    mutationFn: () => api.post(`/affiliates/portal/${token}/messages`, reply),
    onSuccess: () => {
      toast.success('Sent to the admin team');
      setReply({ subject: '', body: '' });
      qc.invalidateQueries({ queryKey: ['affiliate-portal', token] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'That message did not send')),
  });

  const pay = async () => {
    setPaying(true);
    try {
      const { data: res } = await api.post(`/affiliates/portal/${token}/pay`,
        { origin_url: window.location.origin });
      window.location.href = res.url;
    } catch (e) {
      toast.error(getErrorMessage(e, 'Card payment could not be started'));
      setPaying(false);
    }
  };

  if (isLoading) {
    return (
      <FwmShell testId="affiliate-portal-page">
        <section className="mx-auto max-w-[900px] px-6 lg:px-10 py-20 space-y-4">
          <Skeleton className="h-10 w-72" />
          <Skeleton className="h-40 w-full" />
        </section>
      </FwmShell>
    );
  }

  if (isError || !data) {
    return (
      <FwmShell testId="affiliate-portal-page">
        <section className="mx-auto max-w-[700px] px-6 lg:px-10 py-24 text-center">
          <div className="fwm-card p-12" data-testid="affiliate-portal-invalid">
            <AlertTriangle className="h-8 w-8 mx-auto text-amber-600" />
            <h1 className="fwm-display text-2xl mt-5">This link is no longer valid</h1>
            <p className="mt-3 text-sm text-[color:var(--fwm-muted)]">
              Ask the Archbishop's office to send you a fresh portal link.
            </p>
            <Link to="/affiliated-ministries" className="fwm-btn mt-8 inline-flex">
              Affiliate directory
            </Link>
          </div>
        </section>
      </FwmShell>
    );
  }

  const a = data.affiliate;
  const status = STATUS[data.status] || STATUS.pending;
  const StatusIcon = status.icon;
  const open = data.open_invoice;

  return (
    <FwmShell ctaLabel="Affiliate directory" ctaLink="/affiliated-ministries"
              testId="affiliate-portal-page">
      <section className="mx-auto max-w-[900px] px-6 lg:px-10 pt-14 pb-24 space-y-10">
        <div>
          <span className="fwm-kicker">Affiliate portal</span>
          <h1 className="fwm-display text-4xl mt-3" data-testid="portal-org-name">{a.org_name}</h1>
          <div className={`mt-4 inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-semibold ${status.tone}`}
               data-testid="portal-status">
            <StatusIcon className="h-3.5 w-3.5" /> {status.label}
            {a.expires_at && data.status === 'active' && (
              <span className="font-normal opacity-80">
                · renews {new Date(a.expires_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        {open && (
          <div className="fwm-card p-7" data-testid="portal-open-invoice">
            <span className="fwm-kicker">
              {open.kind === 'renewal' ? 'Renewal due' : 'Membership fee'}
            </span>
            <p className="fwm-display text-3xl mt-2">
              {formatCurrency(open.amount, open.currency)}
            </p>
            <p className="mt-2 text-sm text-[color:var(--fwm-muted)]">
              Invoice {open.number}
              {open.due_at ? ` · due ${new Date(open.due_at).toLocaleDateString()}` : ''}
              {open.period_end ? ` · covers until ${new Date(open.period_end).toLocaleDateString()}` : ''}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              {data.stripe_enabled ? (
                <button onClick={pay} disabled={paying} className="fwm-btn"
                        data-testid="portal-pay-btn">
                  {paying ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <CreditCard className="h-4 w-4" />}
                  Pay by card
                </button>
              ) : null}
              <p className="text-xs text-[color:var(--fwm-muted)]">
                Paying by bank transfer or cash? Send a message below and the office will confirm
                your payment for you.
              </p>
            </div>
          </div>
        )}

        <div className="fwm-card p-7" data-testid="portal-certificate">
          <span className="fwm-kicker">Certificate of affiliation</span>
          {data.status === 'active' ? (
            <>
              <p className="mt-2 text-sm text-[color:var(--fwm-muted)]">
                Your certificate is ready to print and frame. It carries your affiliation
                number{a.certificate?.serial ? ` (${a.certificate.serial})` : ''}, the dates it
                covers and a QR code anyone can scan to confirm it is genuine.
              </p>
              <a
                href={`${API_BASE}/affiliates/portal/${token}/certificate.pdf`}
                target="_blank" rel="noreferrer"
                className="fwm-btn mt-6 inline-flex"
                data-testid="portal-certificate-download"
              >
                <Download className="h-4 w-4" /> Download certificate
              </a>
            </>
          ) : (
            <p className="mt-2 text-sm text-[color:var(--fwm-muted)]"
               data-testid="portal-certificate-locked">
              The certificate prints while your affiliation is active and the fee is paid up —
              your account is currently {status.label.toLowerCase()}. Settle the invoice above
              and it unlocks straight away.
            </p>
          )}
        </div>

        {!!data.invoices?.length && (
          <div>
            <h2 className="fwm-display text-2xl">Invoices</h2>
            <div className="fwm-card mt-4 divide-y divide-[color:var(--fwm-line)]"
                 data-testid="portal-invoices">
              {data.invoices.map((i) => (
                <div key={i.id} className="flex flex-wrap items-center justify-between gap-4 px-6 py-4"
                     data-testid={`portal-invoice-${i.number}`}>
                  <div>
                    <p className="text-sm font-medium">{i.number}
                      <span className="text-[color:var(--fwm-muted)] font-normal">
                        {' '}· {i.kind === 'renewal' ? 'Renewal' : 'Membership'}
                      </span>
                    </p>
                    <p className="text-xs text-[color:var(--fwm-muted)] mt-0.5">
                      Issued {i.issued_at ? new Date(i.issued_at).toLocaleDateString() : '—'}
                      {i.paid_at ? ` · paid ${new Date(i.paid_at).toLocaleDateString()}` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm tabular-nums">{formatCurrency(i.amount, i.currency)}</p>
                    <p className={`text-[11px] font-semibold uppercase tracking-[0.12em] mt-0.5 ${
                      i.status === 'paid' ? 'text-emerald-700'
                        : i.status === 'void' ? 'text-slate-400' : 'text-amber-700'}`}>
                      {i.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h2 className="fwm-display text-2xl">Messages</h2>
          <div className="fwm-card mt-4 p-6 space-y-5">
            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-start">
              <div className="space-y-3">
                <Input value={reply.subject} className="fwm-field" placeholder="Subject (optional)"
                       onChange={(e) => setReply((r) => ({ ...r, subject: e.target.value }))}
                       data-testid="portal-message-subject" />
                <Textarea rows={4} value={reply.body} className="fwm-field"
                          placeholder="Write to the admin team…"
                          onChange={(e) => setReply((r) => ({ ...r, body: e.target.value }))}
                          data-testid="portal-message-body" />
              </div>
              <button onClick={() => send.mutate()}
                      disabled={send.isPending || reply.body.trim().length < 2}
                      className="fwm-btn" data-testid="portal-message-send">
                {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Send className="h-4 w-4" />}
                Send
              </button>
            </div>

            {!data.messages?.length ? (
              <p className="flex items-center gap-2 text-sm text-[color:var(--fwm-muted)]"
                 data-testid="portal-messages-empty">
                <Inbox className="h-4 w-4" /> No messages yet.
              </p>
            ) : (
              <div className="divide-y divide-[color:var(--fwm-line)]" data-testid="portal-messages">
                {data.messages.map((m) => (
                  <div key={m.id} className="py-4" data-testid={`portal-message-${m.id}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--fwm-muted)]">
                      {m.direction === 'inbound' ? 'You' : (m.sent_by_name || 'Admin team')}
                      {' · '}{m.kind}
                      {m.created_at ? ` · ${new Date(m.created_at).toLocaleString()}` : ''}
                    </p>
                    {m.subject && <p className="mt-1.5 text-sm font-medium">{m.subject}</p>}
                    <p className="mt-1 text-sm whitespace-pre-wrap text-[color:var(--fwm-muted)]">
                      {m.body}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </FwmShell>
  );
}
